<template>
    <card title="Transfer Request Audit">
        <div class="filter-grid">
            <SearchComponent v-model="storeFilter" label="Store"
                search-u-r-l="/Store" :params="{ active: true }"
                :prepopulate="true" clearable @input="loadItems" />
            <DateRangeFilter v-model="dateRangeFilter" label="Request Date:" @input="loadItems" />
            <b-form-group label="Patient Name">
                <b-form-input v-model="patientNameFilter" placeholder="Search patient..." />
            </b-form-group>
            <div>
                <label>Status:</label>
                <EnumSelector v-model="statusFilter" :enum-codes="transferStatusEnum" clearable @input="loadItems" />
            </div>
            <div>
                <label>Direction:</label>
                <b-form-select v-model="directionFilter" :options="directionOptions" @change="loadItems" />
            </div>
        </div>

        <b-row class="mt-2 mb-2">
            <b-col class="d-flex justify-content-end">
                <b-dropdown variant="outline-secondary" size="sm" text="Columns" right no-caret>
                    <template #button-content>
                        <i class="fa fa-columns"></i> Columns
                    </template>
                    <b-dropdown-form v-for="field in allFields" :key="field.key">
                        <b-form-checkbox v-model="columnVisibility[field.key]" @change="saveColumnPreferences">
                            {{ field.label }}
                        </b-form-checkbox>
                    </b-dropdown-form>
                </b-dropdown>
            </b-col>
        </b-row>

        <b-table striped :items="filteredItems" :fields="visibleFields" :busy="isLoading" show-empty
            empty-text="No transfer requests found." :per-page="pageSize" :current-page="pageNumber"
            sort-by="timeStamp" :sort-desc="true"
            @row-clicked="handleRowClick" tbody-class="cursor-pointer">
            <template #table-busy>
                <div class="text-center my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong>Loading...</strong>
                </div>
            </template>
            <template #cell(transferStatus)="data">
                <b-badge :variant="getStatusVariant(data.value)">
                    {{ getStatusText(data.value) }}
                </b-badge>
            </template>
            <template #cell(inbound)="data">
                {{ data.value ? 'Inbound' : 'Outbound' }}
            </template>
            <template #cell(externalPharmacyName)="data">
                <div>{{ data.item.externalPharmacyResolvedName || data.value }}</div>
                <div v-if="data.item.externalPharmacyFax">
                    <small class="text-muted">Fax: {{ data.item.externalPharmacyFax }}</small>
                </div>
            </template>
        </b-table>

        <BottomPagerBar v-model="pageNumber" :total-rows="totalRows" :per-page="pageSize" show-total
            show-page-size-selector @change-page-size="pageSize = $event"
            item-name="request" :loading="isLoading" />

        <b-modal id="audit-detail-modal" :title="detailModalTitle" size="xl" no-fade scrollable hide-footer>
            <b-overlay :show="isLoadingDetail">
                <b-row>
                    <b-col md="6">
                        <h6>Transfer Info</h6>
                        <table class="table table-sm table-borderless detail-table">
                            <tr><td class="text-muted">Status</td><td>
                                <b-badge :variant="getStatusVariant(activeDetail?.transferStatus)">
                                    {{ getStatusText(activeDetail?.transferStatus) }}
                                </b-badge>
                                <span v-if="activeDetail?.transferStatusReason" class="ml-1">— {{ activeDetail.transferStatusReason }}</span>
                            </td></tr>
                            <tr><td class="text-muted">Direction</td><td>{{ activeDetail?.directionText }}</td></tr>
                            <tr><td class="text-muted">Requested By</td><td>{{ activeDetail?.requestedBy }}</td></tr>
                            <tr><td class="text-muted">Request Date</td><td>{{ formatDateTime(activeDetail?.timeStamp) }}</td></tr>
                            <tr><td class="text-muted">Status Date</td><td>{{ formatDateTime(activeDetail?.transferStatusDate) }}</td></tr>
                            <tr><td class="text-muted">Status Set By</td><td>{{ activeDetail?.transferStatusSetBy }}</td></tr>
                            <tr v-if="activeDetail?.timeToCompletionMinutes != null">
                                <td class="text-muted">Time to Completion</td><td>{{ activeDetail.timeToCompletionMinutes }} min</td>
                            </tr>
                            <tr><td class="text-muted">Transfer Reason</td><td>{{ activeDetail?.rxTransferReasonName }}</td></tr>
                        </table>
                    </b-col>
                    <b-col md="6">
                        <h6>Patient & Pharmacy</h6>
                        <table class="table table-sm table-borderless detail-table">
                            <tr><td class="text-muted">Patient</td><td>{{ activeDetail?.patientName }}</td></tr>
                            <tr v-if="activeDetail?.patientDOB"><td class="text-muted">DOB</td><td>{{ formatDate(activeDetail.patientDOB) }}</td></tr>
                            <tr><td class="text-muted">External Pharmacy</td><td>{{ activeDetail?.externalPharmacyResolvedName || activeDetail?.externalPharmacyName }}</td></tr>
                            <tr v-if="activeDetail?.externalPharmacyPhone"><td class="text-muted">Phone</td><td>{{ activeDetail.externalPharmacyPhone }}</td></tr>
                            <tr v-if="activeDetail?.externalPharmacyFax"><td class="text-muted">Fax</td><td>{{ activeDetail.externalPharmacyFax }}</td></tr>
                            <tr v-if="activeDetail?.externalPharmacistName"><td class="text-muted">Pharmacist</td><td>{{ activeDetail.externalPharmacistName }}</td></tr>
                            <tr v-if="activeDetail?.transferringPharmacist"><td class="text-muted">Transferring Pharmacist</td><td>{{ activeDetail.transferringPharmacist }}</td></tr>
                            <tr v-if="activeDetail?.storeName"><td class="text-muted">Store</td><td>{{ activeDetail.storeName }}</td></tr>
                            <tr v-if="activeDetail?.programName"><td class="text-muted">Program</td><td>{{ activeDetail.programName }}</td></tr>
                        </table>
                    </b-col>
                </b-row>

                <div v-if="detailContents.length" class="mt-3">
                    <h6>Medications</h6>
                    <b-table striped small :items="detailContents" :fields="contentFields" show-empty
                        empty-text="No medications listed." />
                </div>

                <div v-if="detailNotes.length" class="mt-3">
                    <h6>Notes</h6>
                    <div v-for="note in detailNotes" :key="note.id" class="border-bottom py-2">
                        <small class="text-muted">{{ note.createdBy }} — {{ formatDateTime(note.created) }}</small>
                        <div>{{ note.body }}</div>
                    </div>
                </div>

                <div v-if="activeDetail?.requestMessageId || activeDetail?.transferMessageId" class="mt-3">
                    <h6>Electronic Transfer Document</h6>
                    <div :id="pdfContainerId" class="pdf-container">
                        <div v-if="isLoadingPdf" class="text-center my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Loading document...</strong>
                        </div>
                    </div>
                </div>
            </b-overlay>
        </b-modal>
    </card>
</template>

<script lang="ts">
import axios from 'axios';
import { Component, Vue, Watch } from 'vue-property-decorator';
import SearchComponent from '@/components/SearchComponent.vue';
import DateRangeFilter from '@/components/DateRangeFilter.vue';
import EnumSelector from '@/components/EnumSelector.vue';
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import { transferStatus } from '@/models/RxTransfer';
import { Store } from '@/models/Store';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';
import { Note } from '@/models/Note';
import PDFObject from 'pdfobject';
import moment from 'moment';

const COLUMN_PREFS_KEY = 'transferRequestAudit_columnPreferences';

@Component({
    name: 'TransferRequestAuditPage',
    components: {
        SearchComponent,
        DateRangeFilter,
        EnumSelector,
        BottomPagerBar,
    },
})
export default class TransferRequestAuditPage extends Vue {
    protected items: any[] = [];
    protected isLoading = false;
    protected pageSize = 50;
    protected pageNumber = 1;
    protected transferStatusEnum = transferStatus;

    protected storeFilter: Store = new Store();
    protected patientNameFilter: string = '';
    protected dateRangeFilter: any = {
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
    };
    protected statusFilter: any = null;
    protected directionFilter: string = '';

    protected activeDetail: any = null;
    protected detailContents: any[] = [];
    protected detailNotes: Note[] = [];
    protected isLoadingDetail = false;
    protected isLoadingPdf = false;

    protected columnVisibility: Record<string, boolean> = {
        patientName: true,
        drugName: true,
        directionText: true,
        externalPharmacyName: true,
        requestedBy: true,
        timeStamp: true,
        transferStatus: true,
        transferStatusReason: true,
        transferStatusDate: true,
        transferStatusSetBy: true,
        transferringPharmacist: true,
        storeName: true,
        rxTransferReasonName: false,
        programName: false,
        externalPharmacyPhone: false,
        externalPharmacistName: false,
        patientDOB: false,
        timeToCompletionMinutes: false,
    };

    get allFields() {
        return [
            { key: 'patientName', label: 'Patient Name', sortable: true },
            { key: 'drugName', label: 'Drug Name', sortable: true },
            { key: 'directionText', label: 'Direction', sortable: true },
            { key: 'externalPharmacyName', label: 'External Pharmacy', sortable: true },
            { key: 'requestedBy', label: 'Requested By', sortable: true },
            { key: 'timeStamp', label: 'Request Date', formatter: this.formatDateTime, sortable: true },
            { key: 'transferStatus', label: 'Status', sortable: true },
            { key: 'transferStatusReason', label: 'Status Reason', sortable: true },
            { key: 'transferStatusDate', label: 'Status Date', formatter: this.formatDateTime, sortable: true },
            { key: 'transferStatusSetBy', label: 'Status Set By', sortable: true },
            { key: 'transferringPharmacist', label: 'Transferring Pharmacist', sortable: true },
            { key: 'storeName', label: 'Store', sortable: true },
            { key: 'rxTransferReasonName', label: 'Transfer Reason', sortable: true },
            { key: 'programName', label: 'Program', sortable: true },
            { key: 'externalPharmacyPhone', label: 'Ext. Pharmacy Phone', sortable: true },
            { key: 'externalPharmacistName', label: 'External Pharmacist', sortable: true },
            { key: 'patientDOB', label: 'Patient DOB', formatter: this.formatDate, sortable: true },
            { key: 'timeToCompletionMinutes', label: 'Time to Completion (min)', sortable: true },
        ];
    }

    get visibleFields() {
        return this.allFields.filter(f => this.columnVisibility[f.key] !== false);
    }

    get filteredItems() {
        if (!this.patientNameFilter?.trim()) return this.items;
        const search = this.patientNameFilter.trim().toLowerCase();
        return this.items.filter((item: any) => {
            const name = item.patientName || '';
            return name.toLowerCase().includes(search);
        });
    }

    get totalRows() {
        return this.filteredItems?.length ?? 0;
    }

    get directionOptions() {
        return [
            { value: '', text: 'All Directions' },
            { value: 'false', text: 'Outbound' },
            { value: 'true', text: 'Inbound' },
        ];
    }

    created() {
        this.pageSize = getPageSizePreference(this, 'pageSize_TransferRequestAudit', 50);
        this.loadColumnPreferences();
        this.loadItems();
    }

    @Watch('pageSize')
    onPageSizeChanged() {
        this.pageNumber = 1;
        savePageSizePreference(this, 'pageSize_TransferRequestAudit', this.pageSize);
    }

    async loadItems() {
        this.isLoading = true;
        this.pageNumber = 1;
        try {
            const params: any = {};

            if (this.storeFilter?.id) {
                params.storeId = this.storeFilter.id;
            }
            if (this.dateRangeFilter?.startDate) {
                params.startDate = new Date(this.dateRangeFilter.startDate);
            }
            if (this.dateRangeFilter?.endDate) {
                params.endDate = new Date(this.dateRangeFilter.endDate);
            }
            if (this.statusFilter !== null && this.statusFilter !== '' && this.statusFilter !== undefined) {
                params.transferStatus = parseInt(this.statusFilter);
            }
            if (this.directionFilter !== '') {
                params.inbound = this.directionFilter === 'true';
            }

            const res = await axios.get('RxTransfer/GetAudit', { params });
            this.items = res.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
        this.isLoading = false;
    }

    formatDateTime(v: string | Date) {
        if (!v) return '';
        return moment.utc(v).local().format('L LT');
    }

    formatDate(v: string | Date) {
        if (!v) return '';
        return moment.utc(v).local().format('L');
    }

    getStatusText(status: number) {
        return transferStatus[status] ?? 'Unknown';
    }

    getStatusVariant(status: number): string {
        switch (status) {
            case transferStatus.New: return 'info';
            case transferStatus.Open: return 'primary';
            case transferStatus.Cancelled: return 'danger';
            case transferStatus.Completed: return 'success';
            default: return 'secondary';
        }
    }

    get contentFields() {
        return [
            { key: 'medicationName', label: 'Medication' },
            { key: 'rxNumber', label: 'Rx Number' },
            { key: 'ndc', label: 'NDC' },
        ];
    }

    get detailModalTitle() {
        if (!this.activeDetail) return 'Transfer Request Details';
        const status = this.getStatusText(this.activeDetail.transferStatus);
        return `Transfer Request #${this.activeDetail.id} — ${status}`;
    }

    get pdfContainerId() {
        return `audit-pdf-${this.activeDetail?.id}`;
    }

    async handleRowClick(row: any) {
        this.activeDetail = row;
        this.detailContents = [];
        this.detailNotes = [];
        this.isLoadingDetail = true;
        this.isLoadingPdf = false;
        this.$bvModal.show('audit-detail-modal');

        try {
            const response = await axios.get(`RxTransfer/${row.id}/GetByTransferId`);
            this.detailContents = response.data.contents || [];
            this.detailNotes = response.data.notes || [];
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
        this.isLoadingDetail = false;

        if (row.requestMessageId || row.transferMessageId) {
            this.loadTransferPdf(row.id);
        }
    }

    async loadTransferPdf(transferId: number) {
        this.isLoadingPdf = true;
        try {
            const response = await axios.get(`RxTransfer/${transferId}/MessageData`, { responseType: 'blob' });
            if (response.data) {
                const objectUrl = URL.createObjectURL(response.data);
                PDFObject.embed(objectUrl, `#${this.pdfContainerId}`, {
                    pdfOpenParams: { view: 'FitW', pagemode: 'thumbs', toolbar: 1, scrollbar: 0 },
                });
            }
        } catch {
            // No document available — this is normal for non-electronic transfers
        }
        this.isLoadingPdf = false;
    }

    loadColumnPreferences() {
        try {
            const saved = localStorage.getItem(COLUMN_PREFS_KEY);
            if (saved) {
                const prefs = JSON.parse(saved);
                Object.keys(prefs).forEach(key => {
                    if (key in this.columnVisibility) {
                        this.columnVisibility[key] = prefs[key];
                    }
                });
            }
        } catch {
            // ignore invalid stored preferences
        }
    }

    saveColumnPreferences() {
        localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify(this.columnVisibility));
    }
}
</script>

<style scoped>
.filter-grid {
    display: grid;
    gap: 15px;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
}

@media (min-width: 1600px) {
    .filter-grid {
        grid-template-columns: repeat(4, 1fr);
    }
}

.detail-table td:first-child {
    width: 160px;
    white-space: nowrap;
}

.pdf-container {
    height: 500px;
}

::v-deep(.cursor-pointer) {
    cursor: pointer;
}
</style>
